// =============================================================================
// DASHBOARD COMMERCIAL - Composant Devis & Factures
// =============================================================================
// Gestion des devis et suivi facturation Pennylane pour un dossier

import RacNegatifDevis from "./RacNegatifDevis";
import { useState, useEffect } from "react";
import {
  Receipt,
  Plus,
  FileText,
  Send,
  CheckCircle2,
  Clock,
  AlertCircle,
  Download,
  Archive,
  RefreshCw,
  Euro,
  XCircle,
  ExternalLink,
  Pencil,
  PenTool,
  FilePlus
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { formatDateFR } from "../../utils/dateFormat";
import { isInstallateurProfile, hasDevisCatalogue, hasIntegrationPennylane } from "../../utils/tenantProfile";
import { openAuthedPdf } from "./dossier/helpers";
import SendDevisOmvModal from "../../components/omv/SendDevisOmvModal";
import DevisEnvoiTrace from "../../components/omv/DevisEnvoiTrace";
import { devisStatutLabel } from "../../utils/devisStatut";
import { useConfigList, configLabel } from "../../hooks/useConfigList";
import DevisStatutOmvSelect from "../../components/omv/DevisStatutOmvSelect";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { gabaritsDevisSelectionnables, gabaritDevisParDefaut } from "./gabaritsPdfDevis";
import { enteteTenant } from "../../utils/enteteTenant";
import { nombreFR } from "../../utils/formatNombre";

const API = process.env.REACT_APP_BACKEND_URL;

// Configuration des statuts devis (aligné avec le backend)
// Statuts réels du backend: brouillon, emis, signe, refuse, expire, annule
// Chaque statut porte sa PAIRE fond (`color`) + texte (`text`) contrastée
// dual-mode. Sans `text` explicite, l'ancien rendu forçait
// `text-[var(--oh-accent-contrast)]` (blanc) sur des fonds pâles → illisible
// en mode clair. Les fonds Tailwind solides legacy (red/yellow/amber-500)
// sont repassés sur les tokens `--oh-stat-*` (dual-mode).
const STATUTS_DEVIS = {
  draft: { label: "Brouillon", color: "bg-zinc-500/20 border border-[var(--oh-border)]", text: "text-[var(--oh-text)]", icon: Pencil },
  brouillon: { label: "Brouillon", color: "bg-zinc-500/20 border border-[var(--oh-border)]", text: "text-[var(--oh-text)]", icon: Pencil },
  locked: { label: "Verrouillé", color: "bg-[var(--oh-accent-solid)]", text: "text-[var(--oh-accent-contrast)]", icon: CheckCircle2 },
  emis: { label: "Émis", color: "bg-[var(--oh-accent-solid)]", text: "text-[var(--oh-accent-contrast)]", icon: Send },
  genere: { label: "Généré", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", text: "text-[var(--oh-success-text)]", icon: CheckCircle2 },
  signe: { label: "Signé", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", text: "text-[var(--oh-success-text)]", icon: CheckCircle2 },
  devis_signe: { label: "Signé", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", text: "text-[var(--oh-success-text)]", icon: CheckCircle2 },
  dossier_depose: { label: "Dossier déposé", color: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]", text: "text-[var(--oh-accent-text)]", icon: Send },
  paye: { label: "Payé", color: "bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)]", text: "text-[var(--oh-info-text)]", icon: CheckCircle2 },
  refuse: { label: "Refusé", color: "bg-[var(--oh-stat-red-bg)] border border-[var(--oh-stat-red-border)]", text: "text-[var(--oh-stat-red-text)]", icon: XCircle },
  expire: { label: "Expiré", color: "bg-[var(--oh-stat-yellow-bg)] border border-[var(--oh-stat-yellow-border)]", text: "text-[var(--oh-stat-yellow-text)]", icon: Clock },
  annule: { label: "Annulé", color: "bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)]", text: "text-[var(--oh-text-secondary)]", icon: XCircle },
  archive: { label: "Archivé", color: "bg-zinc-500/20 border border-[var(--oh-border)]", text: "text-[var(--oh-text)]", icon: Archive }
};

const TYPE_DOC_LABELS = {
  devis_amo: "Devis AMO",
  contrat_amo: "Contrat AMO",
  mandat_amo: "Mandat ANAH",
};

// Configuration des statuts facturation
const STATUTS_FACTURATION = {
  brouillon: { label: "Brouillon", color: "bg-zinc-500/20 border border-[var(--oh-border)]", text: "text-[var(--oh-text)]" },
  pret: { label: "Prêt pour Pennylane", color: "bg-[var(--oh-accent-solid)]", text: "text-[var(--oh-accent-contrast)]" },
  envoye_pennylane: { label: "Envoyé", color: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]", text: "text-[var(--oh-accent-text)]" },
  facture_creee: { label: "Facture créée", color: "bg-[var(--oh-accent-solid)]", text: "text-[var(--oh-accent-contrast)]" },
  paiement_partiel: { label: "Paiement partiel", color: "bg-[var(--oh-stat-amber-bg)] border border-[var(--oh-stat-amber-border)]", text: "text-[var(--oh-stat-amber-text)]" },
  paye: { label: "Payé", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", text: "text-[var(--oh-success-text)]" },
  erreur: { label: "Erreur", color: "bg-[var(--oh-stat-red-bg)] border border-[var(--oh-stat-red-border)]", text: "text-[var(--oh-stat-red-text)]" }
};

export default function DevisFacturesTab({ dossierId, tenantId, dossier, onNavigate }) {
  // Profil installateur (OMV/Climat) : workflow devis CEE simplifié — « Valider »
  // au lieu d'« Émettre », pas de signature manuelle ni DocuSign (V2), bouton
  // « Envoyer » présent (placeholder V2). TEF (amo) garde le flux signature.
  const isInstall = isInstallateurProfile();
  // Liste éditable des statuts devis OMV (labels/ordre) — couche au-dessus de l'enum.
  const { items: statutsDevis } = useConfigList("statuts_devis");
  // Source UNIQUE des libellés de facturation (pipeline Pennylane). Repli = map
  // STATUTS_FACTURATION en dur. #4/#5 : l'ancienne liste "statuts_facturation"
  // (sémantique a_facturer/facturé) est supprimée → pas de 2e vérité.
  const { items: statutsFacturation } = useConfigList("statuts_facturation_pennylane");
  const [sendDevisModal, setSendDevisModal] = useState({ open: false, devis: null });
  const [loading, setLoading] = useState(true);
  const [devisList, setDevisList] = useState([]);
  const [facturationList, setFacturationList] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [generatingMandat, setGeneratingMandat] = useState(false);
  
  // Templates PDF Design Studio
  const [pdfTemplates, setPdfTemplates] = useState([]);
  const [selectedPdfTemplate, setSelectedPdfTemplate] = useState(null);
  
  // Formulaire création devis
  const [newDevis, setNewDevis] = useState({
    objet: "",
    lignes: [],
    conditions_paiement: "",
    notes: ""
  });
  
  // Ligne en cours d'édition
  const [currentLigne, setCurrentLigne] = useState({
    designation: "",
    quantite: 1,
    unite: "u",
    prix_unitaire_ht: 0,
    taux_tva: 5.5,
    type_ligne: "service"
  });

  // Stabilize dependency: use length instead of array référence to prevent re-render loops
  const devisIdsCount = dossier?.devis_ids?.length || 0;
  useEffect(() => {
    if (dossierId) {
      loadDocuments();
      loadPdfTemplates();
    }
  }, [dossierId, devisIdsCount]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return { 
      ...enteteTenant(tenantId),
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  // Charger les templates PDF Design Studio
  const loadPdfTemplates = async () => {
    try {
      const res = await fetch(`${API}/api/metier/devis-template/templates`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        // Z23 — jamais le gabarit FACTURE dans le sélecteur d'un devis, et le
        // gabarit présélectionné est celui que le SERVEUR rend par défaut
        // (`defaut_rendu`), pas « le premier is_default de la liste ».
        setPdfTemplates(gabaritsDevisSelectionnables(data.templates));
        const defaultTemplate = gabaritDevisParDefaut(data.templates);
        if (defaultTemplate) {
          setSelectedPdfTemplate(defaultTemplate.id);
        }
      }
    } catch (error) {
      console.error("Erreur chargement templates PDF:", error);
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    const headers = getAuthHeaders();
    const isTEF = dossier?.activite === "tef" || tenantId === "tef";
    try {
      // For TEF: load devis_amo and contrat_amo from TEF endpoint
      if (isTEF) {
        try {
          const tefRes = await fetch(
            `${API}/api/metier/tef/dossiers/${dossierId}/devis-contrats`,
            { headers, credentials: 'include' }
          );
          if (tefRes.ok) {
            const data = await tefRes.json();
            // CC-20 : la Demande d'avance ANAH est listée uniquement dans
            // Préparer & Signer + Documents Générés. Elle ne doit pas
            // apparaître dans Devis & Factures (cf. fix régression placeholder
            // "Générer Devis AMO + Contrat AMO" qui ne s'affichait plus quand
            // devisList contenait uniquement la demande d'avance).
            const tefDocs = (data.documents || [])
              .filter(d => d.type_document !== 'demande_avance_anah')
              .map(d => ({
                ...d,
                id: d.id || d._id,
                type_document: d.type_document || 'devis_amo',
                // 🔑 N°501 — LE STATUT D'AFFICHAGE ÉCRASE LE STATUT RÉEL, et
                // c'est voulu (ligne suivante). On CONSERVE donc l'original :
                // l'émission doit router sur le MONDE du devis (« draft » =
                // metier_hub/devis), ⛔ pas sur l'étiquette montrée à l'écran.
                statut_brut: d.statut,
                // Fix: statut "genere" si le doc a été auto-généré (statut genere OU pdf_genere OU source auto)
                statut: (d.statut === 'genere' || d.pdf_genere || d.pdf_url) ? 'genere' : (d.statut || 'genere'),
                // Fix: utiliser le numero OU le label du type comme nom d'affichage
                numero: d.numero || TYPE_DOC_LABELS[d.type_document] || d.type_document,
              }));
            setDevisList(tefDocs);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error("Erreur chargement devis TEF:", e);
        }
      }

      // CLIMAT/fallback: Charger les devis depuis la nouvelle API metier_devis
      const devisIds = dossier?.devis_ids || [];
      
      if (devisIds.length > 0) {
        // Charger chaque devis par son ID
        const devisPromises = devisIds.map(async (id) => {
          try {
            const res = await fetch(`${API}/api/metier/devis/${id}`, { headers, credentials: 'include' });
            if (res.ok) {
              return await res.json();
            }
            return null;
          } catch {
            return null;
          }
        });
        
        const devisResults = await Promise.all(devisPromises);
        setDevisList(devisResults.filter(d => d !== null));
      } else {
        // Fallback: chercher les devis par dossier_id
        const devisRes = await fetch(
          `${API}/api/metier/devis?dossier_id=${dossierId}`,
          { headers, credentials: 'include' }
        );
        if (devisRes.ok) {
          setDevisList(await devisRes.json());
        } else {
          setDevisList([]);
        }
      }

      // Charger données facturables (ancien système)
      const factRes = await fetch(
        `${API}/api/metier/documents/dossier/${dossierId}/facturation`,
        { headers, credentials: 'include' }
      );
      if (factRes.ok) {
        setFacturationList(await factRes.json());
      }
    } catch (err) {
      console.error("Erreur chargement documents:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatMontant = (montant) => {
    if (!montant && montant !== 0) return "-";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR"
    }).format(montant);
  };

  const formatDate = formatDateFR;

  // Actions sur devis
  const handleEmettreDevis = async (devisId, statutDevis) => {
    setActionLoading(devisId);
    try {
      const authHeaders = getAuthHeaders();
      // Profil installateur (OMV) : émission via le monde metier_hub/devis
      // (DRAFT→EMIS, numéro manuel PRÉSERVÉ). Le chemin documents/ ne parse pas
      // ces devis (statut "draft" + schéma de lignes différent). TEF/Climat
      // conservent leur flux documents/ inchangé.
      //
      // 🔴🔴 N°501 — LE DISCRIMINANT EST LE DEVIS, ⛔ PAS LE PROFIL DU TENANT.
      // ⚠️ MESURÉ le 20/09 : un devis créé par l'écran TERRAIN sur `mario_bat`
      // (statut « draft », lignes produit) partait sur le chemin documents/,
      // qui répondait **400 « Devis non trouvé »** — alors que le devis EXISTE
      // et s'affiche dans la carte juste au-dessus. Mario ⛔ ne pouvait PAS
      // valider son devis : dernier geste de la chaîne V1, et il était mort.
      // 🔑 LA RAISON EST ÉCRITE DANS LE COMMENTAIRE CI-DESSUS — « ne parse pas
      // ces devis (statut "draft") » — mais la CONDITION, elle, interrogeait le
      // profil CEE. Or `mario_bat` ⛔ ne pose PAS de CEE : l'inscrire dans
      // `PROFILS_POSE_CEE` aurait été FAUX (fiche CEE, moteur cumac) et aurait
      // soigné le symptôme en cassant le sens.
      // ⇒ On route sur ce qui décide VRAIMENT : la NATURE du devis. Le profil
      // installateur reste accepté tel quel — ⛔ aucun changement pour OMV, et
      // TEF/Climat (statuts « brouillon »/« emis ») gardent le chemin documents.
      const natifDevis = isInstallateurProfile() || statutDevis === "draft";
      const emettreUrl = natifDevis
        ? `${API}/api/metier/devis/${devisId}/emettre`
        : `${API}/api/metier/documents/devis/${devisId}/emettre`;
      const res = await fetch(
        emettreUrl,
        { method: "POST", headers: authHeaders }
      );
      if (res.ok) {
        await loadDocuments();
      } else {
        const err = await res.json();
        alert(`Erreur: ${err.detail}`);
      }
    } catch (err) {
      console.error("Erreur émission:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSignerDevis = async (devisId) => {
    setActionLoading(devisId);
    try {
      const authHeaders = getAuthHeaders();
      const res = await fetch(
        `${API}/api/metier/documents/devis/${devisId}/signer`,
        { method: "POST", headers: authHeaders }
      );
      if (res.ok) {
        await loadDocuments();
      } else {
        const err = await res.json();
        alert(`Erreur: ${err.detail}`);
      }
    } catch (err) {
      console.error("Erreur signature:", err);
    } finally {
      setActionLoading(null);
    }
  };

  // 🔴🔴 N°690-1 — SUPPRIMER UN BROUILLON, DEPUIS L'ÉCRAN.
  // ⚠️ Aujourd'hui il fallait une lane pour retirer un devis DRAFT créé par
  // erreur — et la référence restait dans `dossier.devis_ids` (10 références
  // MORTES mesurées en base le 25/09).
  // 🔑 LA GARDE EST CÔTÉ SERVEUR (un devis ÉMIS ou NUMÉROTÉ refuse, avec son
  // motif) : ce bouton ⛔ n'est PAS la protection, il en est la porte.
  // ⛔ On DEMANDE avant : une suppression ⛔ ne se rattrape pas.
  const handleSupprimerDevis = async (devis) => {
    const nom = devis.numero || devis.numero_provisoire || "ce devis";
    if (!window.confirm(
      `Supprimer définitivement le brouillon ${nom} ?\n\n`
      + "Il sera retiré du dossier. Cette action ne s'annule pas.")) return;
    setActionLoading(devis.id);
    try {
      const res = await fetch(`${API}/api/metier/devis/${devis.id}`,
                              { method: "DELETE", headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`Brouillon ${nom} supprimé`);
        await loadDocuments();
      } else {
        // ⛔ Le motif du SERVEUR, en clair — ⛔ pas un « erreur » muet.
        toast.error(data.detail || `Suppression refusée (erreur ${res.status})`);
      }
    } catch (err) {
      toast.error("Réseau indisponible — le devis n'a pas été supprimé.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchiveZoho = async (devisId) => {
    setActionLoading(devisId);
    try {
      const authHeaders = getAuthHeaders();
      const res = await fetch(
        `${API}/api/metier/documents/devis/${devisId}/archive-zoho`,
        { method: "POST", headers: authHeaders }
      );
      const data = await res.json();
      if (res.ok) {
        alert(`Archivé avec succès: ${data.zoho_folder_path}`);
        await loadDocuments();
      } else {
        alert(`Erreur: ${data.detail}`);
      }
    } catch (err) {
      console.error("Erreur archivage:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPdf = async (devisId, templateId = null) => {
    // Utilisation de fetch avec headers pour éviter "Tenant non identifié"
    setActionLoading(devisId);
    try {
      const authHeaders = getAuthHeaders();
      
      // Construire l'URL avec le template si spécifié
      const templateParam = templateId || selectedPdfTemplate;
      let url = `${API}/api/metier/documents/devis/${devisId}/pdf`;
      if (templateParam) {
        url += `?template_id=${templateParam}`;
      }
      
      const response = await fetch(url, { 
        headers: authHeaders 
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Erreur ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `devis_${devisId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      console.error("Erreur téléchargement:", err);
      alert(`Erreur: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Téléchargement PDF pour devis TEF (utilise pdf_url directement)
  const handleDownloadTefPdf = async (pdfUrl, typeDoc, devisId) => {
    setActionLoading(devisId);
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${API}${pdfUrl}`, { headers: authHeaders });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // BUG-03 fix 2026-05-03 — message backend 503 mentionne deja Regenerer
        throw new Error(errorData.detail || `Erreur ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${typeDoc}_${devisId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      console.error("Erreur téléchargement TEF:", err);
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Voir le PDF TEF = OUVRIR à l'écran (nouvel onglet), pas télécharger en
  // silence. Passe par openAuthedPdf (pop-up-safe : onglet ouvert dans le
  // geste utilisateur avant l'await du fetch authentifié).
  const handleViewTefPdf = async (pdfUrl, devisId) => {
    setActionLoading(devisId);
    await openAuthedPdf({
      url: `${API}${pdfUrl}`,
      headers: getAuthHeaders(),
      onError: (msg) => toast.error(msg),
    });
    setActionLoading(null);
  };

  // Voir un devis générique (endpoint /documents/devis/{id}/pdf) à l'écran.
  const handleViewPdf = async (devisId, templateId = null) => {
    const templateParam = templateId || selectedPdfTemplate;
    let url = `${API}/api/metier/documents/devis/${devisId}/pdf`;
    if (templateParam) url += `?template_id=${templateParam}`;
    setActionLoading(devisId);
    await openAuthedPdf({
      url,
      headers: getAuthHeaders(),
      onError: (msg) => toast.error(`Erreur: ${msg}`),
    });
    setActionLoading(null);
  };

  // BUG-03 fix 2026-05-03 — Régénération PDF AMO TEF (devis_amo/contrat_amo/mandat_amo)
  // Recall l'endpoint generer correspondant pour reupload Zoho + stocker pdf_data b64.
  const handleRegenerateAmoPdf = async (typeDoc, devisId) => {
    setActionLoading(devisId);
    try {
      const authHeaders = getAuthHeaders();
      let endpoint;
      // Fix : contrat_amo doit appeler SON générateur (contrat-amo/generer),
      // pas devis-amo/generer — sinon « Régénérer » sur un contrat régénérait
      // un devis. Un doc = un endpoint de génération dédié.
      if (typeDoc === "devis_amo") {
        endpoint = `${API}/api/metier/tef/dossiers/${dossierId}/devis-amo/generer`;
      } else if (typeDoc === "contrat_amo") {
        endpoint = `${API}/api/metier/tef/dossiers/${dossierId}/contrat-amo/generer`;
      } else if (typeDoc === "mandat_amo") {
        endpoint = `${API}/api/metier/tef/dossiers/${dossierId}/mandat-amo/generer`;
      } else {
        toast.error(`Type non régénérable : ${typeDoc}`);
        return;
      }
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({}),
        credentials: "include",
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Erreur ${response.status}`);
      }
      toast.success("PDF régénéré avec succès — rechargez la page pour voir le nouveau document.");
      if (typeof onRefresh === "function") onRefresh();
    } catch (err) {
      console.error("Erreur régénération PDF AMO:", err);
      toast.error(`Régénération échouée : ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Créer données facturables
  const handleCreerFacturation = async (devisId) => {
    setActionLoading(devisId);
    try {
      const authHeaders = getAuthHeaders();
      const res = await fetch(
        `${API}/api/metier/documents/facturation/depuis-devis/${devisId}`,
        { method: "POST", headers: authHeaders }
      );
      if (res.ok) {
        await loadDocuments();
      } else {
        const err = await res.json();
        alert(`Erreur: ${err.detail}`);
      }
    } catch (err) {
      console.error("Erreur création facturation:", err);
    } finally {
      setActionLoading(null);
    }
  };

  // Création de devis
  const addLigne = () => {
    if (!currentLigne.designation) return;
    
    const montant_ht = currentLigne.quantite * currentLigne.prix_unitaire_ht;
    const montant_tva = montant_ht * currentLigne.taux_tva / 100;
    
    setNewDevis({
      ...newDevis,
      lignes: [...newDevis.lignes, {
        ...currentLigne,
        montant_ht,
        montant_tva,
        montant_ttc: montant_ht + montant_tva
      }]
    });
    
    setCurrentLigne({
      designation: "",
      quantite: 1,
      unite: "u",
      prix_unitaire_ht: 0,
      taux_tva: 5.5,
      type_ligne: "service"
    });
  };

  const removeLigne = (idx) => {
    setNewDevis({
      ...newDevis,
      lignes: newDevis.lignes.filter((_, i) => i !== idx)
    });
  };

  const handleCreateDevis = async () => {
    if (newDevis.lignes.length === 0) {
      alert("Ajoutez au moins une ligne au devis");
      return;
    }
    
    setActionLoading("create");
    try {
      const res = await fetch(`${API}/api/metier/devis`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          dossier_id: dossierId,
          ...newDevis
        })
      });
      
      if (res.ok) {
        setShowCreateModal(false);
        setNewDevis({ objet: "", lignes: [], conditions_paiement: "", notes: "" });
        await loadDocuments();
      } else {
        const err = await res.json();
        alert(`Erreur: ${err.detail || "Erreur lors de la création du devis"}`);
      }
    } catch (err) {
      console.error("Erreur création:", err);
      alert("Erreur de connexion");
    } finally {
      setActionLoading(null);
    }
  };

  // Calcul totaux nouveau devis
  const calculateTotals = () => {
    let totalHT = 0;
    let totalTVA = 0;
    
    newDevis.lignes.forEach(l => {
      totalHT += l.montant_ht;
      totalTVA += l.montant_tva;
    });
    
    return {
      totalHT,
      totalTVA,
      totalTTC: totalHT + totalTVA
    };
  };

  // Ne montrer le spinner que lors du premier chargement (pas de flash au switch de tab)
  if (loading && devisList.length === 0 && facturationList.length === 0) {
    return (
      <div className="flex items-center justify-center h-40">
        <RefreshCw className="w-6 h-6 text-[var(--oh-text-muted)] animate-spin" />
      </div>
    );
  }

  const totals = calculateTotals();

  // FICCA reference : pour TEF, le Mandat ANAH doit toujours être visible
  // dans la liste Devis & Factures — soit via une card existante (mandat_amo
  // en metier_devis) soit via une placeholder card avec bouton Générer.
  const isTEF = dossier?.activite === "tef" || tenantId === "tef";
  const hasMandat = devisList.some(d => d.type_document === "mandat_amo");

  const handleGenerateMandat = async () => {
    setGeneratingMandat(true);
    try {
      const res = await fetch(
        `${API}/api/metier/tef/dossiers/${dossierId}/mandat-amo/generer`,
        { method: "POST", headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.success("Mandat ANAH généré", {
          description: `Mandataire : ${data?.mandataire?.raison_sociale || "—"}`,
        });
        await loadDocuments();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail?.message || err?.detail || "Erreur génération du Mandat ANAH");
      }
    } catch (e) {
      console.error("Erreur génération mandat:", e);
      toast.error(`Erreur réseau : ${e.message}`);
    } finally {
      setGeneratingMandat(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header avec bouton création */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[var(--oh-text)] font-medium">Devis & Factures</h3>
          <p className="text-[var(--oh-text-muted)] text-sm">
            {devisList.length} devis • {facturationList.length} en facturation
          </p>
        </div>
        {!dossier?.verrouille && dossier?.activite !== "tef" && (
          <Button 
            onClick={() => {
              // Utiliser le wizard si onNavigate est disponible
              if (onNavigate) {
                onNavigate("devis-wizard", { dossierId, devisId: "new" });
              } else {
                setShowCreateModal(true);
              }
            }}
            className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
            data-testid="create-devis-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau devis
          </Button>
        )}
      </div>

      {/* Section DEVIS */}
      <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-[var(--oh-text)] font-medium flex items-center gap-2">
            <FileText className="w-4 h-4 text-[var(--oh-accent-text)]" />
            Devis
          </h4>
          
          {/* Sélecteur de template PDF — 🔴 N°657 : `tenantId !== "tef"` était
              une GARDE EN CREUX. ⚠️ MESURÉ : TEF POSSÈDE 2 `devis_templates`
              (rangés sous son ObjectId) et la route les lui sert — retirer le
              slug SANS rien mettre à la place les lui AFFICHERAIT. ⇒ on gate
              par la capacité DÉCLARÉE qui porte ce parcours : `devis_catalogue`
              (climatpac ✅, omv_france ✅, mario_bat ✅ — tous l'avaient déjà ;
              tef ⛔ ne la déclare PAS, il ⛔ ne l'avait pas non plus).
              ⛔ Aucun slug, ⛔ aucune visibilité changée. */}
          {pdfTemplates.length > 0 && hasDevisCatalogue() && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--oh-text-muted)]">Template PDF:</span>
              <select
                value={selectedPdfTemplate || ""}
                onChange={(e) => setSelectedPdfTemplate(e.target.value || null)}
                className="bg-[var(--oh-bg-elevated)] text-[var(--oh-text)] text-xs rounded px-2 py-1 border border-[var(--oh-border)]"
                data-testid="pdf-template-selector"
              >
                {pdfTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nom} {(typeof t.defaut_rendu === "boolean" ? t.defaut_rendu : t.is_default) ? "(défaut)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        {devisList.length === 0 ? (
          <div className="py-2">
            {/* Point 2 (style 2/3) — CTA groupés EN TÊTE, alignés à gauche (plus
                « perdus au centre »). Un seul générateur AMO (wizard DevisAMOForm,
                pages/metier/DevisAMOForm.jsx : include_ergo, client_overrides,
                extra_line) au lieu d'un POST naïf. */}
            {isTEF && onNavigate && (
              <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="contrats-cta-row">
                <Button
                  data-testid="generate-devis-amo-btn"
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => onNavigate("devis-wizard", { dossierId, devisId: "new" })}
                >
                  <FilePlus className="w-4 h-4 mr-1" />
                  Générer Devis AMO + Contrat AMO
                </Button>
                <Button
                  data-testid="generate-mandat-amo-btn"
                  size="sm"
                  variant="outline"
                  className="border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]"
                  onClick={handleGenerateMandat}
                  disabled={generatingMandat}
                >
                  {generatingMandat ? (
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <PenTool className="w-4 h-4 mr-1" />
                  )}
                  {hasMandat ? "Régénérer Mandat ANAH" : "Générer Mandat ANAH"}
                </Button>
              </div>
            )}
            <div className="flex items-center gap-2 text-[var(--oh-text-muted)] text-sm">
              <Receipt className="w-4 h-4" /> Aucun devis généré pour l'instant.
            </div>
            {isTEF && !onNavigate && (
              <p className="text-[var(--oh-info-text)] text-xs mt-2">
                Navigation indisponible — ouvrez le dossier depuis le hub Commercial.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {/* FICCA : Mandat ANAH placeholder card si absent (TEF only) */}
            {isTEF && !hasMandat && (
              <div
                className="bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)] rounded-lg p-4 flex items-center justify-between"
                data-testid="mandat-placeholder-card"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[var(--oh-info-bg)]">
                    <PenTool className="w-5 h-5 text-[var(--oh-info-text)]" />
                  </div>
                  <div>
                    <p className="text-[var(--oh-text)] font-medium">Mandat ANAH</p>
                    <p className="text-[var(--oh-text-muted)] text-sm">
                      Cerfa 15923*01 — généré et signé par TEF (mandataire). Pas de DocuSign.
                    </p>
                  </div>
                </div>
                <Button
                  data-testid="generate-mandat-amo-btn"
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={handleGenerateMandat}
                  disabled={generatingMandat}
                >
                  {generatingMandat ? (
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <FilePlus className="w-4 h-4 mr-1" />
                  )}
                  Générer Mandat ANAH
                </Button>
              </div>
            )}
            {devisList.map((devis) => {
              const statutInfo = STATUTS_DEVIS[devis.statut] || STATUTS_DEVIS.brouillon;
              const StatusIcon = statutInfo.icon;
              const isLoading = actionLoading === devis.id;
              
              return (
                <div 
                  key={devis.id}
                  className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 hover:bg-[var(--oh-bg-elevated)] transition-all"
                  data-testid={`devis-card-${devis.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[var(--oh-text)] font-medium">
                          {devis.numero || TYPE_DOC_LABELS[devis.type_document] || devis.numero_provisoire || "Devis"}
                        </span>
                        {devis.type_document && TYPE_DOC_LABELS[devis.type_document] && (
                          <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] border border-[var(--oh-info-border)] text-xs">
                            {TYPE_DOC_LABELS[devis.type_document]}
                          </Badge>
                        )}
                        {devis.is_principal && (
                          <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] border border-[var(--oh-info-border)] text-xs">
                            ★ Principal
                          </Badge>
                        )}
                        <Badge className={`${statutInfo.color} ${statutInfo.text || "text-[var(--oh-text)]"} text-xs`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {devisStatutLabel(devis, statutsDevis, statutInfo.label)}
                        </Badge>
                        {devis.zoho_archived && (
                          <Badge className="bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)] text-xs">
                            <Archive className="w-3 h-3 mr-1" />
                            Archivé
                          </Badge>
                        )}
                      </div>
                      <p className="text-[var(--oh-text-muted)] text-sm mb-1">
                        {devis.objet || devis.client_nom || "Sans objet"}
                      </p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-[var(--oh-text-secondary)]">
                          Créé le {formatDate(devis.date_creation || devis.created_at)}
                        </span>
                        {devis.date_emission && (
                          <span className="text-[var(--oh-accent-text)]">
                            Émis le {formatDate(devis.date_emission)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-lg font-medium text-[var(--oh-text)]">
                        {formatMontant(devis.total_ttc || devis.montant_ttc)}
                      </p>
                      {(devis.aides_deduites > 0 || devis.snapshot_aides?.total_aides > 0) && (
                        <p className="text-sm text-[var(--oh-success-text)]">
                          -{formatMontant(devis.aides_deduites || devis.snapshot_aides?.total_aides)} aides
                        </p>
                      )}
                      {(devis.reste_a_charge > 0 || devis.snapshot_aides?.reste_a_charge > 0) && (
                        <p className="text-sm text-[var(--oh-info-text)] font-medium">
                          RAC: {formatMontant(devis.reste_a_charge || devis.snapshot_aides?.reste_a_charge)}
                        </p>
                      )}
                      <RacNegatifDevis devis={devis} formatMontant={formatMontant} />
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--oh-border)]">
                    {/* PDF — OUVRE le document à l'écran (nouvel onglet), plus de
                        téléchargement silencieux. Le téléchargement reste dispo via
                        le bouton dédié ci-dessous. */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (devis.pdf_url) {
                          handleViewTefPdf(devis.pdf_url, devis.id);
                        } else if (["devis_amo", "contrat_amo", "mandat_amo"].includes(devis.type_document)) {
                          toast.error("PDF AMO indisponible — cliquez sur « Régénérer » pour relancer la génération.");
                        } else {
                          handleViewPdf(devis.id);
                        }
                      }}
                      className="text-[var(--oh-text-muted)] hover:text-[var(--oh-text)]"
                      title="Ouvrir le PDF à l'écran"
                      data-testid={`view-pdf-${devis.id}`}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      PDF
                    </Button>

                    {/* Télécharger le PDF (action distincte de « Voir »). */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (devis.pdf_url) {
                          handleDownloadTefPdf(devis.pdf_url, devis.type_document || "devis", devis.id);
                        } else if (["devis_amo", "contrat_amo", "mandat_amo"].includes(devis.type_document)) {
                          toast.error("PDF AMO indisponible — cliquez sur « Régénérer » pour relancer la génération.");
                        } else {
                          handleDownloadPdf(devis.id);
                        }
                      }}
                      className="text-[var(--oh-text-muted)] hover:text-[var(--oh-text)]"
                      title="Télécharger le PDF"
                      data-testid={`download-pdf-${devis.id}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Télécharger
                    </Button>

                    {/* BUG-03 fix 2026-05-03 — Régénérer PDF AMO si Zoho indispo / b64 absent */}
                    {["devis_amo", "contrat_amo", "mandat_amo"].includes(devis.type_document) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRegenerateAmoPdf(devis.type_document, devis.id)}
                        disabled={actionLoading === devis.id}
                        className="text-[var(--oh-text-muted)] hover:text-[var(--oh-text)]"
                        title="Régénérer le PDF (utile si Zoho indisponible)"
                        data-testid={`regenerate-amo-${devis.id}`}
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${actionLoading === devis.id ? "animate-spin" : ""}`} />
                        Régénérer
                      </Button>
                    )}
                    
                    {/* Vue document (PDF) — ouverture par défaut d'un devis (pas le
                        wizard). Exclut les docs AMO TEF (flux PDF/Zoho dédié). */}
                    {onNavigate && !["devis_amo", "contrat_amo"].includes(devis.type_document) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onNavigate("devis-document", { dossierId, devisId: devis.id })}
                        className="border-[var(--oh-border)] text-[var(--oh-text-secondary)] hover:text-[var(--oh-text)]"
                        data-testid={`view-devis-${devis.id}`}
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Voir le document
                      </Button>
                    )}

                    {/* Actions selon statut - PAS pour les docs TEF auto-générés */}
                    {(devis.statut === "brouillon" || devis.statut === "draft") && !["devis_amo", "contrat_amo"].includes(devis.type_document) && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (onNavigate) {
                              onNavigate("devis-wizard", { dossierId, devisId: devis.id });
                            }
                          }}
                          disabled={isLoading}
                          className="border-[var(--oh-border)] text-[var(--oh-text-muted)] hover:text-[var(--oh-text)]"
                          data-testid={`edit-devis-${devis.id}`}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Modifier
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleEmettreDevis(
                            devis.id, devis.statut_brut ?? devis.statut)}
                          disabled={isLoading}
                          className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                          data-testid={`emit-devis-${devis.id}`}
                        >
                          {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : (isInstall ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <Send className="w-4 h-4 mr-1" />)}
                          {isInstall ? "Valider" : "Émettre"}
                        </Button>
                      </>
                    )}
                    
                    {devis.statut === "emis" && (
                      <>
                        {/* Profil installateur (CEE) : devis « Validé », pas de
                            signature manuelle ni DocuSign. « Envoyer » ouvre le
                            modal d'envoi par email (template Design Studio + PDF
                            en PJ + variables résolues, sender OMV). */}
                        {isInstall ? (
                          <div className="flex flex-col gap-1">
                            <Button
                              size="sm"
                              onClick={() => setSendDevisModal({ open: true, devis })}
                              className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                              data-testid={`send-devis-${devis.id}`}
                            >
                              <Send className="w-4 h-4 mr-1" />
                              {devis.email_envoi?.status === "sent" ? "Renvoyer" : "Envoyer"}
                            </Button>
                            <DevisEnvoiTrace envoi={devis.email_envoi} />
                            {/* Statut manuel post-envoi (Signé→Payé) — couche OMV. */}
                            <DevisStatutOmvSelect
                              devis={devis}
                              tenantId={tenantId}
                              statutsList={statutsDevis}
                              onChanged={() => loadDocuments()}
                            />
                          </div>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleSignerDevis(devis.id)}
                              disabled={isLoading}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`sign-devis-${devis.id}`}
                            >
                              {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                              Signer (manuel)
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                if (onNavigate) {
                                  onNavigate("docusign-send", {
                                    devisId: devis.id,
                                    devisNumero: devis.numero,
                                    clientEmail: dossier?.client_email,
                                    clientNom: dossier?.client_nom || dossier?.client_prenom
                                  });
                                }
                              }}
                              disabled={isLoading}
                              className="border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]"
                              data-testid={`docusign-devis-${devis.id}`}
                            >
                              <PenTool className="w-4 h-4 mr-1" />
                              DocuSign
                            </Button>
                          </>
                        )}
                        {!devis.zoho_archived && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleArchiveZoho(devis.id)}
                            disabled={isLoading}
                            className="border-[var(--oh-accent)] text-[var(--oh-accent-text)]"
                          >
                            <Archive className="w-4 h-4 mr-1" />
                            Archiver
                          </Button>
                        )}
                        {/* 🔴 N°690-1 — SUPPRIMER, et SEULEMENT un brouillon.
                            ⛔ Un devis ÉMIS ⛔ n'affiche PAS ce bouton — et le
                            SERVEUR le refuserait de toute façon, avec son
                            motif : la garde ⛔ n'est PAS dans l'écran. */}
                        {["draft", "brouillon"].includes(devis.statut) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSupprimerDevis(devis)}
                            disabled={isLoading}
                            data-testid="devis-supprimer"
                            className="border-[var(--oh-stat-red-border)] text-[var(--oh-stat-red-text)]"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Supprimer
                          </Button>
                        )}
                      </>
                    )}
                    
                    {/* Statut signature DocuSign en cours */}
                    {devis.statut === "signature_envoyee" && (
                      <div className="flex items-center gap-2">
                        <Badge className="bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]">
                          <Clock className="w-3 h-3 mr-1" />
                          En attente signature DocuSign
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => loadDocuments()}
                          className="text-[var(--oh-text-muted)]"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    
                    {devis.statut === "signe" && (
                      <Button
                        size="sm"
                        onClick={() => handleCreerFacturation(devis.id)}
                        disabled={isLoading || facturationList.some(f => f.devis_source_id === devis.id)}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid={`create-facturation-${devis.id}`}
                      >
                        {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Euro className="w-4 h-4 mr-1" />}
                        Préparer facturation
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Section FACTURATION */}
      {/* 🔴 ETI-113-B — suivi Pennylane : intégration ACTIVE seulement */}
      {hasIntegrationPennylane() && facturationList.length > 0 && (
        <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-info-border)] rounded-xl p-5">
          <h4 className="text-[var(--oh-text)] font-medium mb-4 flex items-center gap-2">
            <Euro className="w-4 h-4 text-[var(--oh-info-text)]" />
            Suivi Facturation Pennylane
          </h4>
          
          <div className="space-y-3">
            {facturationList.map((fact) => {
              const statutInfo = STATUTS_FACTURATION[fact.statut] || STATUTS_FACTURATION.brouillon;
              
              return (
                <div 
                  key={fact.id}
                  className="bg-[var(--oh-bg-elevated)] rounded-lg p-4"
                  data-testid={`facturation-card-${fact.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[var(--oh-text)] font-medium">
                          {fact.reference_interne}
                        </span>
                        <Badge className={`${statutInfo.color} ${statutInfo.text || "text-[var(--oh-text)]"} text-xs`}>
                          {configLabel(statutsFacturation, fact.statut, statutInfo.label)}
                        </Badge>
                      </div>
                      <p className="text-[var(--oh-text-muted)] text-sm">
                        Depuis devis {fact.devis_numero || "-"}
                      </p>
                      {fact.pennylane_numero && (
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]">
                            Pennylane: {fact.pennylane_numero}
                          </Badge>
                          {fact.pennylane_url && (
                            <a
                              href={fact.pennylane_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[var(--oh-accent-text)] hover:underline text-sm flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Voir
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="text-right">
                      <p className="text-lg font-medium text-[var(--oh-text)]">
                        {formatMontant(fact.total_ttc)}
                      </p>
                      {fact.montant_paye > 0 && (
                        <p className="text-sm text-[var(--oh-success-text)]">
                          Payé: {formatMontant(fact.montant_paye)}
                        </p>
                      )}
                      {fact.montant_restant_du > 0 && (
                        <p className="text-sm text-[var(--oh-info-text)]">
                          Reste: {formatMontant(fact.montant_restant_du)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal création devis */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)]">Nouveau devis</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Objet */}
            <div>
              <label className="text-[var(--oh-text-muted)] text-sm mb-1 block">Objet du devis</label>
              <Input
                value={newDevis.objet}
                onChange={(e) => setNewDevis({ ...newDevis, objet: e.target.value })}
                placeholder="Ex: Installation pompe à chaleur air/eau"
                className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
              />
            </div>
            
            {/* Ajout de lignes */}
            <div className="bg-[var(--oh-bg-surface)] rounded-lg p-4">
              <h5 className="text-[var(--oh-text)] text-sm font-medium mb-3">Ajouter une ligne</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-2">
                <div className="col-span-3">
                  <Input
                    value={currentLigne.designation}
                    onChange={(e) => setCurrentLigne({ ...currentLigne, designation: e.target.value })}
                    placeholder="Désignation"
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-sm"
                  />
                </div>
                <div>
                  <Input
                    type="number"
                    value={currentLigne.quantite}
                    onChange={(e) => setCurrentLigne({ ...currentLigne, quantite: parseFloat(e.target.value) || 0 })}
                    placeholder="Qté"
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-sm"
                  />
                </div>
                <div>
                  <Input
                    type="number"
                    value={currentLigne.prix_unitaire_ht}
                    onChange={(e) => setCurrentLigne({ ...currentLigne, prix_unitaire_ht: parseFloat(e.target.value) || 0 })}
                    placeholder="P.U. HT"
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-sm"
                  />
                </div>
                <div>
                  <select
                    value={currentLigne.taux_tva}
                    onChange={(e) => setCurrentLigne({ ...currentLigne, taux_tva: parseFloat(e.target.value) })}
                    className="w-full h-9 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-md text-[var(--oh-text)] text-sm px-2"
                  >
                    <option value={5.5}>5.5%</option>
                    <option value={10}>10%</option>
                    <option value={20}>20%</option>
                  </select>
                </div>
              </div>
              <Button
                onClick={addLigne}
                size="sm"
                className="mt-2 bg-[var(--oh-bg-elevated)]"
                disabled={!currentLigne.designation}
              >
                <Plus className="w-3 h-3 mr-1" />
                Ajouter
              </Button>
            </div>
            
            {/* Lignes ajoutées */}
            {newDevis.lignes.length > 0 && (
              <div className="border border-[var(--oh-border)] rounded-lg overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead className="bg-[var(--oh-bg-elevated)]">
                    <tr>
                      <th className="text-left text-[var(--oh-text-muted)] p-2">Désignation</th>
                      <th className="text-right text-[var(--oh-text-muted)] p-2">Qté</th>
                      <th className="text-right text-[var(--oh-text-muted)] p-2">P.U. HT</th>
                      <th className="text-right text-[var(--oh-text-muted)] p-2">TVA</th>
                      <th className="text-right text-[var(--oh-text-muted)] p-2">Total HT</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {newDevis.lignes.map((ligne, idx) => (
                      <tr key={idx} className="border-t border-[var(--oh-border)]">
                        <td className="p-2 text-[var(--oh-text)]">{ligne.designation}</td>
                        <td className="p-2 text-right text-[var(--oh-text-muted)]">{ligne.quantite}</td>
                        <td className="p-2 text-right text-[var(--oh-text-muted)]">{nombreFR(ligne.prix_unitaire_ht, 2)} €</td>
                        <td className="p-2 text-right text-[var(--oh-text-muted)]">{ligne.taux_tva}%</td>
                        <td className="p-2 text-right text-[var(--oh-text)] font-medium">{nombreFR(ligne.montant_ht, 2)} €</td>
                        <td className="p-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLigne(idx)}
                            className="text-[var(--oh-error-text)] hover:text-[var(--oh-error-text)] p-1"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Totaux */}
                <div className="bg-[var(--oh-bg-elevated)] p-3 text-right space-y-1">
                  <p className="text-[var(--oh-text-muted)]">Total HT: <span className="text-[var(--oh-text)] font-medium">{nombreFR(totals.totalHT, 2)} €</span></p>
                  <p className="text-[var(--oh-text-muted)]">Total TVA: <span className="text-[var(--oh-text)] font-medium">{nombreFR(totals.totalTVA, 2)} €</span></p>
                  <p className="text-lg text-[var(--oh-text)] font-medium">Total TTC: {nombreFR(totals.totalTTC, 2)} €</p>
                </div>
              </div>
            )}
            
            {/* Conditions et notes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[var(--oh-text-muted)] text-sm mb-1 block">Conditions de paiement</label>
                <Input
                  value={newDevis.conditions_paiement}
                  onChange={(e) => setNewDevis({ ...newDevis, conditions_paiement: e.target.value })}
                  placeholder="Ex: 30% commande, 70% livraison"
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                />
              </div>
              <div>
                <label className="text-[var(--oh-text-muted)] text-sm mb-1 block">Notes</label>
                <Input
                  value={newDevis.notes}
                  onChange={(e) => setNewDevis({ ...newDevis, notes: e.target.value })}
                  placeholder="Notes internes ou client"
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateModal(false)}
              className="border-[var(--oh-border)]"
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreateDevis}
              disabled={newDevis.lignes.length === 0 || actionLoading === "create"}
              className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
              data-testid="confirm-create-devis"
            >
              {actionLoading === "create" ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Créer le devis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal aperçu PDF supprimé - Utiliser uniquement le téléchargement PDF direct */}

      {/* Brique B — envoi du devis OMV par email (profil installateur) */}
      <SendDevisOmvModal
        open={sendDevisModal.open}
        onClose={() => setSendDevisModal({ open: false, devis: null })}
        devis={sendDevisModal.devis}
        tenantId={tenantId}
        onSent={() => loadDocuments()}
      />
    </div>
  );
}
